/**
 * Utility functions for working with SVG content
 */

interface SVGDimensions {
  width: number
  height: number
}

/**
 * Extract width and height dimensions from an SVG template string
 *
 * @param svgTemplate - The SVG template string to parse
 * @param defaultWidth - Default width to use if parsing fails (defaults to 600)
 * @param defaultHeight - Default height to use if parsing fails (defaults to 600)
 * @returns An object with width and height properties
 */
export function extractSVGDimensions(
  svgTemplate: string,
  defaultWidth = 600,
  defaultHeight = 600,
): SVGDimensions {
  try {
    // Parse dimensions from SVG
    const svgDoc = new DOMParser().parseFromString(svgTemplate, 'image/svg+xml')
    const svgElement = svgDoc.documentElement

    // Check for parsing errors
    const parseError = svgDoc.querySelector('parsererror')
    if (parseError) {
      return {
        width: defaultWidth,
        height: defaultHeight,
      }
    }

    // Check if the document is an SVG
    if (svgElement.tagName.toLowerCase() !== 'svg') {
      return {
        width: defaultWidth,
        height: defaultHeight,
      }
    }

    // Get width and height attributes, or use viewBox if available
    let width = svgElement.getAttribute('width')
    let height = svgElement.getAttribute('height')

    // If width/height are not specified, try to get from viewBox
    if ((!width || !height) && svgElement.getAttribute('viewBox')) {
      const viewBox = svgElement.getAttribute('viewBox').split(' ')
      if (viewBox.length === 4) {
        width = viewBox[2]
        height = viewBox[3]
      }
    }

    return {
      width: parseInt(width || defaultWidth.toString()),
      height: parseInt(height || defaultHeight.toString()),
    }
  } catch (error) {
    // Default fallback dimensions
    return {
      width: defaultWidth,
      height: defaultHeight,
    }
  }
}
